import React, { createContext, useState, ReactNode, useContext } from "react";

// Define and EXPORT the type for breadcrumb items
export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  isActive?: boolean;
}

// Define and EXPORT the type for custom actions
export interface CustomAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

// Define and EXPORT the type for page data
export interface PageData {
  title: string;
  icon?: React.ReactNode | null;
  breadcrumbs?: BreadcrumbItem[];
  showBackButton?: boolean;
  customActions?: CustomAction[] | null;
  [key: string]: any;
}

// Define and EXPORT the context type
export interface PageDataContextType {
  pageData: PageData;
  setPageData: (data: PageData | ((prev: PageData) => PageData)) => void;
  updatePageData: (data: Partial<PageData>) => void;
  resetPageData: () => void;
}

// Initial state
const initialState: PageData = {
  title: "",
  icon: null,
  breadcrumbs: [],
  showBackButton: false,
  customActions: null,
};

// Create the context
export const PageDataContext = createContext<PageDataContextType | undefined>(undefined);

// Define props for the provider
export interface PageDataProviderProps {
  children: ReactNode;
}

// Create and EXPORT the provider component
export const PageDataProvider: React.FC<PageDataProviderProps> = ({ children }) => {
  const [pageData, setPageData] = useState<PageData>(initialState);

  const updatePageData = (data: Partial<PageData>) => {
    setPageData(prev => ({
      ...prev,
      ...data,
    }));
  };

  const resetPageData = () => {
    setPageData(initialState);
  };

  const value: PageDataContextType = {
    pageData,
    setPageData,
    updatePageData,
    resetPageData,
  };

  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
};

// Create and EXPORT the custom hook for using the context
export const usePageData = (): PageDataContextType => {
  const context = useContext(PageDataContext);
  if (!context) {
    throw new Error('usePageData must be used within a PageDataProvider');
  }
  return context;
};